import React from "react";
import axios from "axios";
import toast from "react-hot-toast";

import styles from "../../../styles/AccountModals.module.css";
import { BASE_URL } from "../../../utils/constants";
import Spinner from "../../../components/spinner";
import { handleApiError } from "../../../utils/handleApiError";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import Button from "../../../components/button-v1";
import Input from "../../../components/ui/input";
const AddStaffModal = ({
  isOpen,
  onClose,
  comp_renderer,
  current_render,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required.")
      .max(100, "Name must be at most 100 characters."),
    email: z
      .string()
      .trim()
      .min(1, "Email is required.")
      .email("Invalid email.")
      .max(254, "Email must be at most 254 characters."),
    phone_number: z
      .string()
      .trim()
      .min(1, "Phone is required")
      .refine(
        (val) => val.replace(/\D/g, "").length === 10,
        "Phone must be 10 digits."
      ),
    account_position: z
      .number()
      .int()
      .min(1, "Position is required.")
      .default(1),
    account_role: z.number().int().min(1, "Role is required.").default(2),
    is_admin: z.boolean().default(false),
    active: z.boolean().default(true),
  });

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone_number: "",
      account_position: 1,
      account_role: 2,
      is_admin: false,
      active: true,
    },
  });

  const getToken = () => localStorage.getItem("authToken");
  const getAccountId = () => sessionStorage.getItem("acc_id");

  const locationsQuery = useQuery({
    queryKey: ["locations", getAccountId()],
    queryFn: async () => {
      const token = getToken();
      const account_id = getAccountId();
      const url = `${BASE_URL}/accounts/${account_id}/locations/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.locations.filter((location) => location.active);
    },
    enabled: isOpen === true,
  });

  const rolesQuery = useQuery({
    queryKey: ["roles", getAccountId()],
    queryFn: async () => {
      const token = getToken();
      const account_id = getAccountId();
      const url = `${BASE_URL}/accounts/${account_id}/roles/?staff_roles=true`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: isOpen === true,
  });

  const positionsQuery = useQuery({
    queryKey: ["positions", getAccountId()],
    queryFn: async () => {
      const token = getToken();
      const account_id = getAccountId();
      const url = `${BASE_URL}/accounts/${account_id}/positions/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: isOpen === true,
  });

  const { mutateAsync: addStaff, isLoading: isAdding } = useMutation({
    mutationFn: async (finalFormData) => {
      const token = getToken();
      const url = `${BASE_URL}/external_providers/`;
      const response = await axios.post(url, finalFormData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      comp_renderer(!current_render);
      onClose();
      queryClient.invalidateQueries(["external-providers"]);
      toast.success("Staff added successfully!");
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      handleApiError(error);
    },
  });

  const onSubmit = async (values) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const firstError =
        parsed.error.issues[0]?.message || "Please fix the errors.";
      toast.error(firstError);
      return;
    }
    const finalFormData = { ...parsed.data };
    await addStaff(finalFormData);
  };

  const modalLoading =
    locationsQuery.isLoading ||
    rolesQuery.isLoading ||
    positionsQuery.isLoading;
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add External Provider</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {modalLoading ? (
            <Spinner />
          ) : (
            <form
              className={`${styles.comp_wrapper} ${
                isAdding || isSubmitting ? styles.loading : ""
              }`}
            >
              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        type="text"
                        label="Name"
                        placeholder="Name"
                        name={field.name}
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(e && e.target ? e.target.value : e)
                        }
                        onBlur={field.onBlur}
                        error={errors?.name?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <Input
                        type="email"
                        label="Email"
                        placeholder="Email"
                        name={field.name}
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(e && e.target ? e.target.value : e)
                        }
                        onBlur={field.onBlur}
                        error={errors?.email?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <Controller
                    control={control}
                    name="phone_number"
                    render={({ field }) => (
                      <Input
                        type="text"
                        label="Phone"
                        mask={true}
                        maskPattern="(999) 999-9999"
                        placeholder="Phone"
                        name={field.name}
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(e && e.target ? e.target.value : e)
                        }
                        onBlur={field.onBlur}
                        error={errors?.phone_number?.message}
                        className="no-arrows"
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={formHandleSubmit(onSubmit)}
            loading={isAdding || isSubmitting}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddStaffModal;
